import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { TrendingUp, Ship, Droplets, Anchor } from 'lucide-react';

// --------- HELPERS ----------
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

function computeSafeTooltipPosition({ barRect, containerRect, tipW, tipH, prefAbove = true }) {
  const PADDING = 8;
  const OFFSET  = 10;

  // centro da barra relativo ao container
  let x = (barRect.left - containerRect.left) + (barRect.width / 2);

  const yTop    = (barRect.top - containerRect.top) - OFFSET - tipH; // acima
  const yBottom = (barRect.bottom - containerRect.top) + OFFSET;      // abaixo

  let placeAbove = prefAbove;
  let y = placeAbove ? yTop : yBottom;

  // se acima não cabe, tenta abaixo
  if (placeAbove && y < PADDING) {
    placeAbove = false;
    y = yBottom;
  }

  const containerH = containerRect.height;
  // se abaixo não cabe, volta acima
  if (!placeAbove && (y + tipH + PADDING > containerH)) {
    placeAbove = true;
    y = yTop;
  }

  // clamp final
  y = clamp(y, PADDING, Math.max(PADDING, containerH - tipH - PADDING));

  // como usamos translateX(-50%), clamp considerando meia-largura
  const minCenter = PADDING + tipW / 2;
  const maxCenter = containerRect.width - PADDING - tipW / 2;
  x = clamp(x, minCenter, maxCenter);

  return { x, y, placeAbove };
}
// ----------------------------

export default function InvestmentChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBar, setHoveredBar]     = useState(null);
  const [tooltipData, setTooltipData]   = useState(null);   // {year, value, label?}
  const [tooltipPos, setTooltipPos]     = useState(null);   // {x, y, placeAbove}
  const [isMobile, setIsMobile]         = useState(false);

  // Refs
  const scrollRef       = useRef(null);       // wrapper com overflow-x no mobile
  const innerRefDesktop = useRef(null);       // container RELATIVE (desktop)
  const innerRefMobile  = useRef(null);       // container RELATIVE (mobile)
  const tooltipRef      = useRef(null);       // mede tamanho do tooltip

  const investmentData = [
    { year: 2010, value: 3567.5 }, { year: 2011, value: 2255.4 }, { year: 2012, value: 1261.7 },
    { year: 2013, value: 1988.6 }, { year: 2014, value: 1601.7 }, { year: 2015, value: 1068.7 },
    { year: 2016, value: 1172.3 }, { year: 2017, value: 540.1 },  { year: 2018, value: 944.5 },
    { year: 2019, value: 722.5 },  { year: 2020, value: 248.9 },  { year: 2021, value: 559.3 },
    { year: 2022, value: 325.2 },  { year: 2023, value: 1394.0 }, { year: 2024, value: 511.6 }
  ];

  const CHART_HEIGHT = 320; // px
  const maxValue = Math.max(...investmentData.map(d => d.value));
  const getBarHeight = (value) => (value / maxValue) * (CHART_HEIGHT - 20);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedData(investmentData), 300);
    return () => clearTimeout(t);
  }, []);

  // Tooltip com posicionamento seguro (duplo RAF para iOS/Safari)
  const showTooltip = (item, index, targetEl) => {
    const container = (isMobile ? innerRefMobile : innerRefDesktop).current;
    if (!container) return;

    const barRect       = targetEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    setHoveredBar(index);
    setTooltipData({ year: item.year, value: item.value });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tip = tooltipRef.current;
        const tipW = tip ? tip.offsetWidth  : 140;
        const tipH = tip ? tip.offsetHeight : 44;

        const { x, y, placeAbove } = computeSafeTooltipPosition({
          barRect,
          containerRect,
          tipW,
          tipH,
          prefAbove: !isMobile // mobile: abaixo; desktop: acima
        });

        setTooltipPos({ x, y, placeAbove });
      });
    });
  };

  const handleEnter = (item, index) => (e) => showTooltip(item, index, e.currentTarget);
  const handleLeave = () => { setHoveredBar(null); setTooltipData(null); setTooltipPos(null); };

  // Pointer Events (mouse + touch)
  const getBarHandlers = (item, index) => ({
    onPointerDown:  (e) => handleEnter(item, index)(e),              // tap/click
    onPointerEnter: (e) => !isMobile && handleEnter(item, index)(e), // hover desktop
    onPointerMove:  (e) => !isMobile && handleEnter(item, index)(e),
    onPointerLeave: handleLeave,
  });

  // medidas mobile
  const BAR_W_MOBILE    = 18;
  const GAP_MOBILE      = 10;
  const SLOT_W_MOBILE   = BAR_W_MOBILE + GAP_MOBILE;
  const contentWidthMobile = investmentData.length * SLOT_W_MOBILE + 16;
  const touchSafeStyle  = { touchAction: 'manipulation', userSelect: 'none' };

  return (
    <div className="space-y-8">
      <Card className="w-full">
        <CardContent className="p-6">
          {isMobile ? (
            // --- MOBILE: scroll horizontal + container relativo ---
            <div className="overflow-x-auto no-scrollbar" ref={scrollRef} onScroll={handleLeave}>
              <div
                className="relative flex items-end px-4"
                ref={innerRefMobile}
                style={{ height: CHART_HEIGHT, width: contentWidthMobile }}
              >
                {investmentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: SLOT_W_MOBILE }}>
                    <div
                      className="rounded-t transition-all duration-500 cursor-pointer bg-blue-500 hover:bg-blue-600"
                      style={{
                        ...touchSafeStyle,
                        width: BAR_W_MOBILE,
                        height: `${animatedData[index] ? getBarHeight(item.value) : 0}px`,
                        transitionDelay: `${index * 40}ms`,
                        transform: hoveredBar === index ? 'translateY(-2px)' : 'translateY(0)'
                      }}
                      {...getBarHandlers(item, index)}
                    />
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none"
                    style={{
                      left: tooltipPos ? tooltipPos.x : -9999,
                      top: tooltipPos ? tooltipPos.y : -9999,
                      transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                    }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">
                      R$ {tooltipData.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Milhões
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // --- DESKTOP ---
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-1">
                {investmentData.map((item, index) => (
                  <div key={item.year} className="flex-1 flex flex-col items-center justify-end h-full">
                    <div
                      className="w-full max-w-10 rounded-t transition-all duration-500 cursor-pointer bg-blue-500 hover:bg-blue-600"
                      style={{
                        ...touchSafeStyle,
                        height: `${animatedData[index] ? getBarHeight(item.value) : 0}px`,
                        transitionDelay: `${index * 50}ms`,
                        transform: hoveredBar === index ? 'translateY(-2px)' : 'translateY(0)',
                        transformOrigin: 'bottom'
                      }}
                      {...getBarHandlers(item, index)}
                    />
                    <span className="text-xs mt-2 text-gray-500 pt-2">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.x : -9999,
                    top: tooltipPos ? tooltipPos.y : -9999,
                    transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                  }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">
                    R$ {tooltipData.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Milhões
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-blue-50 to-cyan-100">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            Perfil Corretivo e Retomada Gradual
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-white/60 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">Foco em Manutenção e Recuperação (2024)</h4>
            <p className="text-sm text-gray-700">
              Com R$ 511,6 milhões investidos, 2024 marcou um crescimento de quase 60% em relação a 2023. A execução teve um perfil corretivo, priorizando a preservação da infraestrutura existente em vez de grandes projetos novos.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-white/60 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Anchor className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-cyan-800">Dragagens (R$ 211 mi)</h4>
                  <p className="text-sm text-gray-700">41% do total, com destaque para a dragagem do Porto do Rio de Janeiro, para permitir a entrada de navios maiores.</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-white/60 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Droplets className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-800">Bacia Amazônica (R$ 143 mi)</h4>
                  <p className="text-sm text-gray-700">Manutenção da sinalização e dragagens para mitigar os efeitos da severa estiagem e garantir a navegabilidade.</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-white/60 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Ship className="w-8 h-8 text-purple-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-purple-800">Porto de Suape (R$ 51 mi)</h4>
                  <p className="text-sm text-gray-700">10% do total direcionado à modernização e ampliação da capacidade operacional do porto em Pernambuco.</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
